import { ChangeEvent, ComponentProps } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import useControllableState from 'helpers/hooks/useControllableState';
import Typography from '../typography';

export interface DropdownProps extends ComponentProps<'select'> {
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  items: Array<{ label: string; value: string }>;
  label?: string;
  value?: string;
  defaultValue?: string;
}

const DefaultDropdown: React.FC<DropdownProps> = ({
  className = '',
  containerClassName = '',
  labelClassName,
  label,
  items,
  onChange,
  value,
  ...props
}) => {
  const [selectedValue, setSelectedValue] = useControllableState(value);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedValue(value);
    onChange?.(e);
  };

  const containerClassNames = classNames('grid', containerClassName);

  const selectClassName = classNames(
    'absolute font-body z-10 h-10 text-[14px] border-none focus:ring-0 rounded-sm w-full bg-transparent bg-none font-regular leading-loose pl-3 pr-0 py-0',
    className,
  );

  const labelClassNames = classNames('mb-2 text-secondary-black leading-loose font-medium text-[14px]', labelClassName);

  return (
    <div className={containerClassNames}>
      {label && (
        <Typography as="label" className={labelClassNames}>
          {`${label}${props.required ? ' *' : ''}`}
        </Typography>
      )}

      <div className="relative h-10 min-w-[64px] overflow-hidden rounded-sm border border-neutral-500">
        <select className={selectClassName} value={selectedValue} onChange={handleChange} {...props}>
          {items.map(({ label, value }, index) => (
            <option key={index} value={value}>
              {label}
            </option>
          ))}
        </select>

        <ChevronDownIcon
          data-testid="chevron-down-icon"
          className="absolute right-[5px] top-1/2 z-0 h-5 w-[30px] -translate-y-1/2 stroke-1 text-secondary-black"
        />
      </div>
    </div>
  );
};

export default DefaultDropdown;
